import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail } from "lucide-react";
import { Logo } from "../components/Logo";
import api, { formatApiError } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-cream px-4 py-16" data-testid="forgot-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-cream via-cream/60 to-cream" />
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-3xl border border-terra/15 bg-white/90 p-8 shadow-card backdrop-blur-md sm:p-10"
      >
        <Link to="/auth" className="mb-8 flex justify-center" data-testid="forgot-logo-link">
          <Logo size={52} withWordmark={false} />
        </Link>
        {sent ? (
          <div className="text-center" data-testid="forgot-success">
            <Mail size={32} className="mx-auto text-terra" />
            <h1 className="mt-4 font-serif text-2xl font-medium text-ink">Vérifiez votre boîte mail</h1>
            <p className="mt-3 text-sm leading-relaxed text-clay">
              Si un compte APINITY est lié à <strong>{email}</strong>, un mot de passe de secours vient de partir par email — pensez à regarder vos spams.
              Il est valable 1 heure.
            </p>
            <Link to="/auth" data-testid="forgot-back-login" className="mt-6 inline-block rounded-full bg-terra px-6 py-3 text-sm font-semibold text-cream hover:bg-terra-dark">
              Retour à la connexion
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-center font-serif text-3xl font-medium tracking-tight text-ink" data-testid="forgot-title">Mot de passe oublié ?</h1>
            <p className="mt-2 text-center text-sm text-clay">Entrez votre email : nous vous envoyons un mot de passe de secours, que vous changerez ensuite dans Mon Profil.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  required
                  placeholder="vous@exemple.fr"
                  className="w-full rounded-xl border border-terra/20 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-terra"
                />
              </div>
              {error && <p className="rounded-xl bg-rosedust/20 px-4 py-3 text-sm text-terra-dark" data-testid="forgot-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-button"
                className="w-full rounded-full bg-terra px-6 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark disabled:opacity-60"
              >
                {loading ? "Envoi en cours…" : "Envoyer le lien"}
              </button>
            </form>
            <Link to="/auth" data-testid="forgot-back-link" className="mt-6 flex items-center justify-center gap-2 text-sm text-clay transition-colors hover:text-terra">
              <ArrowLeft size={14} /> Retour à la connexion
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
